import { useEffect, useMemo, useState } from 'react'
import { BarChart3, CalendarDays, Download, FileText, ListTree, Loader2, RefreshCw, Users } from 'lucide-react'
import api from '../../utils/api'
import { exportRowsToCsv, exportRowsToExcel } from '../../utils/exportExcel'
import PackageSearchSelect from './PackageSearchSelect'

function formatDateText(date) {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

function addDays(dateText, days) {
  const [year, month, day] = String(dateText).split('-').map((item) => parseInt(item, 10))
  const date = new Date(year, month - 1, day)
  date.setDate(date.getDate() + days)
  return formatDateText(date)
}

function getDefaultStartDate(days) {
  return addDays(formatDateText(new Date()), -(days - 1))
}

function getWeekdayText(dateText) {
  const [year, month, day] = String(dateText).split('-').map((item) => parseInt(item, 10))
  const date = new Date(year, month - 1, day)
  return ['周日', '周一', '周二', '周三', '周四', '周五', '周六'][date.getDay()]
}

function CompareText({ yesterdayValue, lastWeekValue, weekdayText }) {
  return (
    <>
      （
      <span className="cursor-help text-blue-500" title="前一日数据">{yesterdayValue}</span>
      <span className="text-gray-400">/</span>
      <span className="cursor-help text-orange-500" title={`上周${weekdayText}数据`}>{lastWeekValue}</span>
      ）
    </>
  )
}

export default function ChannelNewUserStatsTab() {
  const getDefaultEndDate = () => formatDateText(new Date())
  const [loading, setLoading] = useState(true)
  const [startDate, setStartDate] = useState(getDefaultStartDate(7))
  const [endDate, setEndDate] = useState(getDefaultEndDate())
  const [sourceLabels, setSourceLabels] = useState([])
  const [viewMode, setViewMode] = useState('detail')
  const [filters, setFilters] = useState({ startDate: getDefaultStartDate(7), endDate: getDefaultEndDate(), sourceLabels: [] })
  const [summary, setSummary] = useState({ total_new_users: 0, total_used_users: 0, total_delayed_first_use_users: 0, row_count: 0, source_count: 0 })
  const [daily, setDaily] = useState([])
  const [todayCompare, setTodayCompare] = useState(null)
  const [range, setRange] = useState({ start_date: getDefaultStartDate(7), end_date: getDefaultEndDate() })
  const [sourceOptions, setSourceOptions] = useState([])
  const [csvExporting, setCsvExporting] = useState(false)

  const fetchData = async (nextFilters = filters) => {
    setLoading(true)
    try {
      const response = await api.get('/admin/channel-new-user-stats', {
        params: {
          start_date: nextFilters.startDate,
          end_date: nextFilters.endDate,
          days: 7,
          source_labels: Array.isArray(nextFilters.sourceLabels) && nextFilters.sourceLabels.length > 0
            ? nextFilters.sourceLabels
            : undefined
        }
      })

      if (response.data.success) {
        const data = response.data.data || response.data || {}
        setSummary(data.summary || {})
        setDaily(data.daily || [])
        setTodayCompare(data.today_compare || null)
        setSourceOptions(data.source_options || [])
        setRange({
          start_date: data.start_date || nextFilters.startDate,
          end_date: data.end_date || nextFilters.endDate
        })
      }
    } catch (error) {
      console.error('获取渠道新增用户统计失败:', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData(filters)
  }, [])

  const handleSubmit = (e) => {
    e.preventDefault()
    const nextFilters = { startDate, endDate, sourceLabels }
    setFilters(nextFilters)
    fetchData(nextFilters)
  }

  const handleQuickRange = (nextDays) => {
    const nextStartDate = getDefaultStartDate(nextDays)
    const nextEndDate = getDefaultEndDate()
    setStartDate(nextStartDate)
    setEndDate(nextEndDate)
    const nextFilters = { startDate: nextStartDate, endDate: nextEndDate, sourceLabels }
    setFilters(nextFilters)
    fetchData(nextFilters)
  }

  const handleReset = () => {
    const nextDays = 7
    const nextStartDate = getDefaultStartDate(nextDays)
    const nextEndDate = getDefaultEndDate()
    setStartDate(nextStartDate)
    setEndDate(nextEndDate)
    setSourceLabels([])
    const nextFilters = { startDate: nextStartDate, endDate: nextEndDate, sourceLabels: [] }
    setFilters(nextFilters)
    fetchData(nextFilters)
  }

  const displayRows = useMemo(() => {
    const groupedMap = new Map()
    for (const item of daily || []) {
      const day = String(item.stat_date || '').trim()
      if (!day) continue
      if (!groupedMap.has(day)) {
        groupedMap.set(day, [])
      }
      groupedMap.get(day).push(item)
    }

    const todayText = formatDateText(new Date())
    const compareDaysMap = todayCompare?.compare_days || {}

    return Array.from(groupedMap.entries())
      .sort((a, b) => String(b[0]).localeCompare(String(a[0])))
      .flatMap(([day, items]) => {
        const sortedItems = [...items].sort((a, b) => {
          const countCompare = Number(b.new_user_count || 0) - Number(a.new_user_count || 0)
          if (countCompare !== 0) return countCompare
          return String(a.source_label || '').localeCompare(String(b.source_label || ''))
        })
        const totalCount = sortedItems.reduce((sum, item) => sum + Number(item.new_user_count || 0), 0)
        const totalUsedCount = sortedItems.reduce((sum, item) => sum + Number(item.used_user_count || 0), 0)
        const totalDelayedFirstUseCount = sortedItems.reduce((sum, item) => sum + Number(item.delayed_first_use_count || 0), 0)
        const isCompareRow = day >= addDays(todayText, -2) && day <= todayText
        const yesterdayCompareMap = compareDaysMap[addDays(day, -1)] || {}
        const lastWeekCompareMap = compareDaysMap[addDays(day, -7)] || {}
        const totalYesterdayCount = Object.values(yesterdayCompareMap).reduce((sum, value) => sum + Number(value || 0), 0)
        const totalLastWeekCount = Object.values(lastWeekCompareMap).reduce((sum, value) => sum + Number(value || 0), 0)
        return [
          ...sortedItems.map((item) => ({
            ...item,
            rowType: 'data',
            used_user_count: Number(item.used_user_count || 0),
            delayed_first_use_count: Number(item.delayed_first_use_count || 0),
            unused_user_count: Math.max(0, Number(item.new_user_count || 0) - Number(item.used_user_count || 0)),
            compare_yesterday: isCompareRow ? Number(yesterdayCompareMap[item.source_label] || 0) : null,
            compare_last_week: isCompareRow ? Number(lastWeekCompareMap[item.source_label] || 0) : null
          })),
          {
            rowType: 'total',
            stat_date: day,
            source_label: '当日合计',
            new_user_count: totalCount,
            used_user_count: totalUsedCount,
            delayed_first_use_count: totalDelayedFirstUseCount,
            unused_user_count: Math.max(0, totalCount - totalUsedCount),
            compare_yesterday: isCompareRow ? totalYesterdayCount : null,
            compare_last_week: isCompareRow ? totalLastWeekCount : null
          }
        ]
      })
  }, [daily, todayCompare])

  const totalUnusedUsers = Math.max(0, Number(summary.total_new_users || 0) - Number(summary.total_used_users || 0))

  const handleExport = () => {
    const sourceRows = viewMode === 'detail' ? displayRows : summaryRows
    if (sourceRows.length === 0) {
      return
    }

    const truncated = sourceRows.length > 1000
    const rows = sourceRows.slice(0, 1000).map((item) => ({
      first_col: viewMode === 'detail' ? item.stat_date : '区间汇总',
      source_label: item.source_label || '-',
      new_user_count: Number(item.new_user_count || 0),
      compare_yesterday: item.compare_yesterday != null ? Number(item.compare_yesterday) : '',
      compare_last_week: item.compare_last_week != null ? Number(item.compare_last_week) : '',
      used_user_count: Number(item.used_user_count || 0),
      delayed_first_use_count: Number(item.delayed_first_use_count || 0),
      unused_user_count: Number(item.unused_user_count || 0)
    }))

    exportRowsToExcel({
      fileName: `渠道新增用户统计_${range.start_date}_${range.end_date}${truncated ? '_最新1000行' : ''}`,
      sheetName: '渠道新增用户统计',
      columns: [
        { header: viewMode === 'detail' ? '日期' : '汇总维度', key: 'first_col', width: 14 },
        { header: '渠道', key: 'source_label', width: 24 },
        { header: '新增用户数', key: 'new_user_count', width: 12 },
        { header: '对比前一日', key: 'compare_yesterday', width: 12 },
        { header: '对比上周同日', key: 'compare_last_week', width: 14 },
        { header: '已使用用户数', key: 'used_user_count', width: 14 },
        { header: '后续首次使用人数', key: 'delayed_first_use_count', width: 16 },
        { header: '未使用用户数', key: 'unused_user_count', width: 12 }
      ],
      rows
    })

    if (truncated) {
      alert('数据超过 1000 行，已导出最新 1000 行')
    }
  }

  // 导出 CSV：固定“展开明细”结构全量导出（含每日“当日合计”行），不截断
  const handleExportCsv = () => {
    if (displayRows.length === 0 || csvExporting) {
      return
    }

    setCsvExporting(true)
    // 先让按钮进入“导出中”状态，再生成文件，避免大数据量时点击无反馈
    setTimeout(() => {
      try {
        const rows = displayRows.map((item) => ({
          stat_date: item.stat_date,
          source_label: item.source_label || '-',
          new_user_count: Number(item.new_user_count || 0),
          compare_yesterday: item.compare_yesterday != null ? Number(item.compare_yesterday) : '',
          compare_last_week: item.compare_last_week != null ? Number(item.compare_last_week) : '',
          used_user_count: Number(item.used_user_count || 0),
          delayed_first_use_count: Number(item.delayed_first_use_count || 0),
          unused_user_count: Number(item.unused_user_count || 0)
        }))

        exportRowsToCsv({
          fileName: `渠道新增用户统计_${range.start_date}_${range.end_date}`,
          columns: [
            { header: '日期', key: 'stat_date' },
            { header: '渠道', key: 'source_label' },
            { header: '新增用户数', key: 'new_user_count' },
            { header: '对比前一日', key: 'compare_yesterday' },
            { header: '对比上周同日', key: 'compare_last_week' },
            { header: '已使用用户数', key: 'used_user_count' },
            { header: '后续首次使用人数', key: 'delayed_first_use_count' },
            { header: '未使用用户数', key: 'unused_user_count' }
          ],
          rows
        })
      } catch (error) {
        console.error('导出渠道新增用户统计CSV失败:', error)
        alert('导出失败，请稍后重试')
      } finally {
        setCsvExporting(false)
      }
    }, 0)
  }

  const summaryRows = useMemo(() => {
    const sourceMap = new Map()

    for (const item of daily || []) {
      const label = String(item.source_label || '').trim()
      if (!label) continue

      if (!sourceMap.has(label)) {
        sourceMap.set(label, {
          source_label: label,
          new_user_count: 0,
          used_user_count: 0,
          delayed_first_use_count: 0,
          unused_user_count: 0
        })
      }

      const current = sourceMap.get(label)
      current.new_user_count += Number(item.new_user_count || 0)
      current.used_user_count += Number(item.used_user_count || 0)
      current.delayed_first_use_count += Number(item.delayed_first_use_count || 0)
      current.unused_user_count += Math.max(0, Number(item.new_user_count || 0) - Number(item.used_user_count || 0))
    }

    return Array.from(sourceMap.values()).sort((a, b) => {
      const newCompare = Number(b.new_user_count || 0) - Number(a.new_user_count || 0)
      if (newCompare !== 0) return newCompare

      const usedCompare = Number(b.used_user_count || 0) - Number(a.used_user_count || 0)
      if (usedCompare !== 0) return usedCompare

      const delayedCompare = Number(b.delayed_first_use_count || 0) - Number(a.delayed_first_use_count || 0)
      if (delayedCompare !== 0) return delayedCompare

      return String(a.source_label || '').localeCompare(String(b.source_label || ''))
    })
  }, [daily])

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">渠道新增用户统计</h2>
          <p className="mt-1 text-sm text-gray-500">按天查看各来源渠道的新增用户数，渠道口径按安装包标签归因</p>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2">
            <CalendarDays className="h-4 w-4 text-gray-400" />
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="border-0 bg-transparent text-sm text-gray-700 outline-none"
            />
          </div>

          <span className="text-sm text-gray-400">～</span>

          <div className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2">
            <CalendarDays className="h-4 w-4 text-gray-400" />
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="border-0 bg-transparent text-sm text-gray-700 outline-none"
            />
          </div>

          <PackageSearchSelect
            value={sourceLabels}
            onChange={setSourceLabels}
            options={sourceOptions}
            multiple
            placeholder="全部渠道"
            searchPlaceholder="搜索渠道名"
            className="min-w-[220px]"
          />

          <button type="submit" className="rounded-lg bg-blue-600 px-4 py-2 text-sm text-white hover:bg-blue-700">
            查询
          </button>
          <button
            type="button"
            onClick={() => handleQuickRange(7)}
            className="rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
          >
            最近7天
          </button>
          <button
            type="button"
            onClick={() => handleQuickRange(30)}
            className="rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
          >
            最近30天
          </button>
          <button
            type="button"
            onClick={handleReset}
            className="rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
          >
            重置
          </button>
          <button
            type="button"
            onClick={() => fetchData(filters)}
            className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
          >
            <RefreshCw className="h-4 w-4" />
            刷新
          </button>
          <button
            type="button"
            onClick={handleExport}
            disabled={loading || daily.length === 0}
            className="inline-flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-2 text-sm text-emerald-700 hover:bg-emerald-100 disabled:cursor-not-allowed disabled:border-gray-200 disabled:bg-gray-100 disabled:text-gray-400"
          >
            <Download className="h-4 w-4" />
            导出 Excel
          </button>
          <button
            type="button"
            onClick={handleExportCsv}
            disabled={loading || daily.length === 0 || csvExporting}
            className="inline-flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-2 text-sm text-emerald-700 hover:bg-emerald-100 disabled:cursor-not-allowed disabled:border-gray-200 disabled:bg-gray-100 disabled:text-gray-400"
          >
            <Download className="h-4 w-4" />
            {csvExporting ? '导出中...' : '导出 CSV'}
          </button>
        </form>
      </div>

      <div className="rounded-xl border border-gray-200 bg-white px-5 py-4 text-sm text-gray-600 shadow-sm">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>当前区间：{range.start_date} 至 {range.end_date}</div>
          <div className="inline-flex rounded-lg border border-gray-200 bg-gray-50 p-1">
            <button
              type="button"
              onClick={() => setViewMode('detail')}
              className={`inline-flex items-center gap-2 rounded-md px-3 py-1.5 text-sm ${
                viewMode === 'detail'
                  ? 'bg-white font-medium text-blue-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <ListTree className="h-4 w-4" />
              展开明细
            </button>
            <button
              type="button"
              onClick={() => setViewMode('summary')}
              className={`inline-flex items-center gap-2 rounded-md px-3 py-1.5 text-sm ${
                viewMode === 'summary'
                  ? 'bg-white font-medium text-blue-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <FileText className="h-4 w-4" />
              汇总列表
            </button>
          </div>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-3 xl:grid-cols-6">
        <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
          <div className="text-sm text-gray-500">新增用户总数</div>
          <div className="mt-2 flex items-center gap-2 text-2xl font-bold text-blue-600">
            <Users className="h-5 w-5" />
            {loading ? <Loader2 className="h-6 w-6 animate-spin text-gray-400" /> : summary.total_new_users || 0}
          </div>
        </div>

        <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
          <div className="text-sm text-gray-500">已使用用户数</div>
          <div className="mt-2 flex items-center gap-2 text-2xl font-bold text-emerald-600">
            <Users className="h-5 w-5" />
            {loading ? <Loader2 className="h-6 w-6 animate-spin text-gray-400" /> : summary.total_used_users || 0}
          </div>
        </div>

        <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
          <div className="text-sm text-gray-500">未使用用户数</div>
          <div className="mt-2 flex items-center gap-2 text-2xl font-bold text-amber-600">
            <Users className="h-5 w-5" />
            {loading ? <Loader2 className="h-6 w-6 animate-spin text-gray-400" /> : totalUnusedUsers}
          </div>
        </div>

        <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
          <div className="text-sm text-gray-500">后续首次使用人数</div>
          <div className="mt-2 flex items-center gap-2 text-2xl font-bold text-cyan-600">
            <Users className="h-5 w-5" />
            {loading ? <Loader2 className="h-6 w-6 animate-spin text-gray-400" /> : summary.total_delayed_first_use_users || 0}
          </div>
        </div>

        <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
          <div className="text-sm text-gray-500">渠道数</div>
          <div className="mt-2 flex items-center gap-2 text-2xl font-bold text-emerald-600">
            <BarChart3 className="h-5 w-5" />
            {loading ? <Loader2 className="h-6 w-6 animate-spin text-gray-400" /> : summary.source_count || 0}
          </div>
        </div>

        <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
          <div className="text-sm text-gray-500">统计行数</div>
          <div className="mt-2 text-2xl font-bold text-purple-600">
            {loading ? <Loader2 className="h-6 w-6 animate-spin text-gray-400" /> : summary.row_count || 0}
          </div>
        </div>
      </div>

      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  {viewMode === 'detail' ? '日期' : '汇总维度'}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">渠道</th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">新增用户数</th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">已使用用户数</th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">后续首次使用人数</th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">未使用用户数</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {loading ? (
                <tr>
                  <td colSpan={6} className="px-6 py-10 text-center text-sm text-gray-500">
                    <Loader2 className="mx-auto h-6 w-6 animate-spin text-blue-600" />
                  </td>
                </tr>
              ) : viewMode === 'detail' ? (
                displayRows.length > 0 ? (
                  displayRows.map((item, index) => (
                    <tr
                      key={`${item.stat_date}-${item.source_label}-${item.rowType}-${index}`}
                      className={item.rowType === 'total' ? 'border-t-4 border-gray-300 bg-blue-50' : 'hover:bg-gray-50'}
                    >
                      <td className={`px-6 py-4 text-sm ${item.rowType === 'total' ? 'font-semibold text-gray-900' : 'text-gray-900'}`}>
                        {item.stat_date}
                      </td>
                      <td className={`px-6 py-4 text-sm ${item.rowType === 'total' ? 'font-semibold text-blue-700' : 'text-gray-900'}`}>
                        {item.source_label}
                      </td>
                      <td className={`px-6 py-4 text-sm font-medium ${item.rowType === 'total' ? 'font-bold text-blue-700' : 'text-blue-600'}`}>
                        {item.new_user_count || 0}
                        {item.compare_yesterday !== null && item.compare_last_week !== null ? (
                          <CompareText
                            yesterdayValue={item.compare_yesterday}
                            lastWeekValue={item.compare_last_week}
                            weekdayText={getWeekdayText(addDays(item.stat_date, -7))}
                          />
                        ) : null}
                      </td>
                      <td className={`px-6 py-4 text-sm font-medium ${item.rowType === 'total' ? 'font-bold text-emerald-700' : 'text-emerald-600'}`}>
                        {item.used_user_count || 0}
                      </td>
                      <td className={`px-6 py-4 text-sm font-medium ${item.rowType === 'total' ? 'font-bold text-cyan-700' : 'text-cyan-600'}`}>
                        {item.delayed_first_use_count || 0}
                      </td>
                      <td className={`px-6 py-4 text-sm font-medium ${item.rowType === 'total' ? 'font-bold text-amber-700' : 'text-amber-600'}`}>
                        {item.unused_user_count || 0}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr
                    className="hover:bg-gray-50"
                  >
                    <td colSpan={6} className="px-6 py-10 text-center text-sm text-gray-500">
                      暂无数据
                    </td>
                  </tr>
                )
              ) : summaryRows.length > 0 ? (
                summaryRows.map((item, index) => (
                  <tr
                    key={`${item.source_label}-${index}`}
                    className="hover:bg-gray-50"
                  >
                    <td className="px-6 py-4 text-sm text-gray-500">
                      区间汇总
                    </td>
                    <td className="px-6 py-4 text-sm font-medium text-gray-900">
                      {item.source_label}
                    </td>
                    <td className="px-6 py-4 text-sm font-medium text-blue-600">
                      {item.new_user_count || 0}
                    </td>
                    <td className="px-6 py-4 text-sm font-medium text-emerald-600">
                      {item.used_user_count || 0}
                    </td>
                    <td className="px-6 py-4 text-sm font-medium text-cyan-600">
                      {item.delayed_first_use_count || 0}
                    </td>
                    <td className="px-6 py-4 text-sm font-medium text-amber-600">
                      {item.unused_user_count || 0}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={6} className="px-6 py-10 text-center text-sm text-gray-500">
                    暂无数据
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}
